import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  Building2,
  Lock,
  Mail,
  ArrowRight,
  Loader2,
  User,
  Phone,
  MapPin,
  Store,
  CheckCircle,
} from "lucide-react";

export const SignUp: React.FC = () => {
  const [step, setStep] = useState<"form" | "success">("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Company
  const [companyName, setCompanyName] = useState("");
  const [rnc, setRnc] = useState("");
  const [businessType, setBusinessType] = useState("retail");
  const [industry, setIndustry] = useState("Comercio General");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");

  // Admin
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("/.netlify/functions/register-tenant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName,
          rnc: rnc.toUpperCase(),
          businessType,
          industry,
          phone,
          address,
          city,
          fullName,
          email: email.toLowerCase().trim(),
          password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Error al registrar la empresa.");
      }

      // Auto-login
      const { error: loginErr } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (loginErr) {
        throw new Error(
          "Empresa creada, pero error al iniciar sesión. Intente desde login.",
        );
      }

      setStep("success");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === "success") {
    return (
      <div className="min-h-screen bg-brand-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white p-10 rounded-3xl border border-brand-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center">
          <div className="mx-auto w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-emerald-500/20">
            <CheckCircle className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-2">
            Bienvenido a OsirisBrain
          </h2>
          <p className="text-sm text-gray-500 font-medium mb-6">
            {companyName} ha sido registrada exitosamente.
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center w-full py-4 px-4 text-sm font-bold rounded-2xl text-white bg-gray-900 hover:bg-gray-950 transition-all active:scale-[0.98]"
          >
            Ir al Panel <ArrowRight className="w-4 h-4 ml-2" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white p-10 rounded-3xl border border-brand-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-in fade-in zoom-in-95 duration-500">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-gray-900/20">
            <Building2 className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-2">
            Crear Empresa
          </h2>
          <p className="text-sm text-gray-500 font-medium tracking-wide uppercase">
            Registro Comercial en OsirisBrain ERP
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50/50 border border-red-100 rounded-2xl text-sm text-red-600 font-medium text-center animate-in slide-in-from-top-2 mt-6">
            {error}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Company Info */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Datos de la Empresa
            </h3>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                Nombre de la Empresa
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Store className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                  placeholder="Ej. Carnicería El Corte Perfecto"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  RNC / Cédula
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    value={rnc}
                    onChange={(e) =>
                      setRnc(e.target.value.replace(/[^0-9A-Z-]/g, ""))
                    }
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="130456789"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  Tipo de Negocio
                </label>
                <select
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  className="block w-full px-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                >
                  <option value="retail">Retail / Tienda</option>
                  <option value="restaurant">Restaurante</option>
                  <option value="hotel">Hotel</option>
                  <option value="clinic">Clínica</option>
                  <option value="workshop">Taller</option>
                  <option value="gym">Gimnasio</option>
                  <option value="butcher">Carnicería</option>
                  <option value="distributor">Distribuidora</option>
                  <option value="services">Servicios</option>
                  <option value="other">Otro</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                Industria
              </label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="block w-full px-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                placeholder="Ej. Comercio General"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  Teléfono
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="809-555-0101"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  Ciudad
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="Santo Domingo"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                Dirección
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                  placeholder="Calle Principal #123"
                />
              </div>
            </div>
          </div>

          {/* Admin Info */}
          <div className="space-y-4 pt-4 border-t border-gray-100">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Administrador
            </h3>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                Nombre Completo
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                  placeholder="Juan Pérez"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                  placeholder="admin@empresa.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  Confirmar
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="group relative flex w-full justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-2xl text-white bg-gray-900 hover:bg-gray-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-all active:scale-[0.98] disabled:opacity-70"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  Crear Empresa{" "}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </button>

            <div className="relative flex items-center justify-center my-2">
              <div className="border-t border-gray-200 flex-1"></div>
              <span className="px-3 text-xs text-gray-400 font-medium">o</span>
              <div className="border-t border-gray-200 flex-1"></div>
            </div>

            <Link
              to="/login"
              className="relative flex w-full justify-center py-4 px-4 border-2 border-brand-100 text-sm font-bold rounded-2xl text-gray-700 bg-white hover:bg-brand-100 hover:border-brand-200 focus:outline-none transition-all active:scale-[0.98] text-center"
            >
              Ya tengo cuenta — Iniciar Sesión
            </Link>
          </div>
        </form>

        <div className="text-center pt-4">
          <p className="text-xs text-gray-400">
            Al registrarte, aceptas nuestros términos y condiciones.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
